import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { voyageCursorStyles } from "../../voyage-style/src/voyage-style";
import { classMap } from "lit/directives/class-map";
import "../src/voyage-view-history";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import "../../voyage-tooltip/src/voyage-tooltip-proxy";
import './breadcrumb-navigation';
import {
  ELEMENT_DATA,
  HeavyWeatherLimitElement,
  HeavyWeatherLimits,
  TEU,
  WEATHER_LIMIT_DATA,
  keyMapping,
  wavesMinValues,
  wind50MMinValues,
} from "../heavy-weather-limits-model";
import { _convertElementToKt, _convertElementToMs, _isTEU } from "./util";
import {
  convertNestedValuesToKt,
  convertNestedValuesToMs,
  keysToConvert,
} from "../helper";
import { AuthorizationRoles, AuthService } from "../../../src/services/auth-service";
import "@maersk-global/mds-components-core/mc-button";

@customElement("voyage-heavy-weather-alarm-limits")
export class VoyageHeavyWeatherAlarmLimits extends LitElement {
  @property({ type: Boolean, reflect: true }) shown: boolean = false;
  @state() private _isEditing: boolean = false;
  @state() private _isUpdated: boolean = false;
  @state() private _viewingHistory: boolean = false;
  @state() _loading = true;
  @property({ type: Array }) private _changesData: any = [];
  @property({attribute: false}) private _isValid: boolean = true;
  @property({type: Boolean}) hasWriteAccess: boolean = AuthService.userHasRole(AuthorizationRoles.WeatherLimitWrite);
  @property({type: Boolean}) hasReadAccess: boolean = AuthService.userHasRole(AuthorizationRoles.WeatherLimitRead);

  private _history: Array<{
    Timestamp: string;
    ModifiedBy: string;
    FieldModified: any;
    From: any;
    To: any;
  }> = [];

  constructor() {
    super();
    this._elements = ELEMENT_DATA;

    this._heavyWeatherAlarmLimits = WEATHER_LIMIT_DATA;

    this._onClose = this._onClose.bind(this);
    this._onAccept = this._onAccept.bind(this);
    this._onEditFields = this._onEditFields.bind(this);
    this._onViewHistory = this._onViewHistory.bind(this);
    this._onCancel = this._onCancel.bind(this);
  }

  static get styles() {
    return [
      voyageCursorStyles,
      css`
        :host {
          font-family: MaerskText;
          align-items: center;
        }
        .container {
          position: sticky;
          width: 98vw;
        }
        .content {
          padding-bottom: 1.5rem;
        }
        .table-container {
          width: 98%;
          margin: 0 auto;
          border: 1px solid var(--neutrals-grey-07);
          border-radius: 8px;
          background-color: var(--neutrals-grey-10);
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-left: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 0 auto;
          border: 1px solid var(--neutrals-grey-07);
        }
        th {
          color: black;
          background: var(--neutrals-grey-09);
          text-align: center;
          padding: 8px;
          border-bottom: 1px solid var(--neutrals-grey-07);
          border-right: 1px solid var(--neutrals-grey-07);
          font-size: 18px;
        }
        .header {
          margin-top: 5px;
          color: black;
        }
        .header-right {
          text-align: left;
        }
        .connected {
          text-align: left;
          padding: 12px;
          border-top: none;
          transform: translateX(36px);
          border-left: 1px solid var(--neutrals-grey-07);
          position: relative;
        }
        .connected::before {
          content: "";
          box-sizing: border-box;
          position: absolute;
          bottom: 50%;
          transform: translateX(-15px);
          width: 10px;
          height: 36px;
          border-left: 1px solid var(--neutrals-grey-05, grey);
          border-bottom: 1px solid var(--neutrals-grey-05, grey);
        }
        .policy-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-column-gap: 10px;
        }
        .legend {
          margin-top: 0;
          text-align: right;
          color: var(--neutrals-grey-02, black);
          font-size: 16px;
          font-weight: bold;
        }
        .cell {
          padding-right: 80px;
          width: 400px;
          font-size: 16px;
        }
        .policy-legend {
          grid-column: span 3;
        }

        #footer {
          width: 100%;
          display: flex;
          justify-content: flex-end;
          border-top: 1px solid var(--neutrals-grey-07);
        }
        .footerButton {
          margin-top: 12px;
          margin-bottom: 12px;
          height: 32px;
          min-width: 80px;
          font-size: 14px;
          font-family: var(--component-paragraph-ff, serif);
          padding: 0px 12px 0px 12px;
          background: none;
          border: none;
          outline: none;
          cursor: pointer;
          border-radius: 3px;
        }
        .cancel {
          color: var(--neutrals-grey-02);
          margin-right: 10px;
          border: 1px solid var(--neutrals-grey-06, grey);
        }
        .footerButton.accept {
          background: rgba(20, 20, 20, 1);
          margin-right: 20px;
          color: var(--neutrals-grey-10, white);
        }

        .footerButton.accept.disabled {
          background: rgba(100, 100, 100, 1);
          cursor: not-allowed;
        }
        .radio-button-display {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          padding-bottom: 5px;
          padding-right: 20px;
          margin-left: 19px;
        }
        .radio-button-display p {
          margin: 0 10px 0 0;
        }
        .radio-button-display input[type="radio"] {
          margin: 0 5px;
        }
        input:disabled {
          color: var(--neutrals-grey-04, grey);
        }
        .inputContainer {
          border-top: 1px solid var(--neutrals-grey-07);
          text-align: left;
        }
        .name {
          text-align: left;
          padding: 12px;
          width: 25%;
          color: var(--neutrals-grey-02, black);
          border-top: 1px solid var(--neutrals-grey-07);
        }
        .periodInput {
          display: flex;
          border: none;
          align-items: center;
        }
        .periodInput input {
          background-color: transparent;
          font-size: 16px;
          width: 95px;
        }
        .dropdown {
          box-sizing: border-box;
          border: none;
          border-radius: 2.5px;
          font-family: MaerskText;
          color: var(--neutrals-grey-02, black);
          background-color: var(--neutrals-grey-10, white);
        }
        .numberInput {
          width: 200px;
          text-align: left;
          height: 34px;
          box-sizing: border-box;
          border: none;
          color: var(--neutrals-grey-02, black);
          background-color: unset;
          font-family: MaerskText;
        }
        .numberInput:invalid {
          background: #ffcccb;
          width: 120px;
          border-radius: 5px;
        }
        .inlineInput:invalid {
          background: #ffcccb;
          width: 80px;
          border-radius: 5px;
        }
        .numberInput[invalid]::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: -25px;
          left: 50%;
          transform: translateX(-50%);
          background-color: rgba(0, 0, 0, 0.75);
          color: white;
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          display: none;
        }
        .numberInput[invalid]:hover::after {
          display: block;
        }
        .numberInput:focus-within {
          outline: none;
        }
        .periodInput:focus-within {
          outline: none;
        }
        .inlineInput:focus-within {
          outline: none;
        }
        .dropdown:focus-within {
          outline: none;
        }
        .inlineInput {
          border: none;
          padding-left: 5px;
          color: var(--neutrals-grey-02, black);
          background-color: unset;
          font-size: 14px;
          font-family: MaerskText;
          .size {
            padding-right: 20px;
          }
        }
        .size {
          padding-right: 20px;
        }
        .closeButton {
          position: absolute;
          border: none;
          font-size: 15px;
          top: 21px;
          right: 10px;
          background: white;
          cursor: pointer;
          color: var(--neutrals-grey-04, grey);
        }
        .number-input {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .breadcrumb-container{
          display: inline-block;
          padding: 7px;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: auto;
          margin-left: 18px;
        }
        .padding{
          padding-right: 10px;
        }
        .footer-text{
          color: rgba(0, 150, 135, 1);
          margin-left: 18px;
          padding-top: 5px;
        }
        .footer-text-invalid{
          color: rgba(255, 0, 0, 1);
          margin-left: 18px;
          padding-top: 5px;
        }
        .questionCircle {
          color: red;
        }
        .view-switch{
            margin-right: 20px;
        }
      `,
    ];
  }
  @property({ attribute: false })
  private _oldWeatherLimitsData!: HeavyWeatherLimits;
  @property({ attribute: false }) private _unit: string = "m/s";

  async connectedCallback() {
    super.connectedCallback();
    this._loading = true;
    const WeatherLimitElement =
      await VoyFleetAlarmModel!.getCompanyWeatherLimits();
    // Use WEATHER_LIMIT_DATA as fallback if company_limits is empty
    if (Object.keys(WeatherLimitElement.company_limits).length === 0) {
      this._heavyWeatherAlarmLimits = JSON.parse(
        JSON.stringify(WEATHER_LIMIT_DATA)
      );
    } else {
      // Merge the defaults from WEATHER_LIMIT_DATA with the fetched data
      this._heavyWeatherAlarmLimits = {
        ...JSON.parse(JSON.stringify(WEATHER_LIMIT_DATA)),
        ...WeatherLimitElement.company_limits,
      };
    }
    this._oldWeatherLimitsData = JSON.parse(
      JSON.stringify(WeatherLimitElement.company_limits)
    );
    this._loading = false;
  }
  private _goToCustomView(){
    this._onClose();
    const showCustomLimit = new CustomEvent('open-custom-limits', {
          detail: {
              isEditView: false
            },
            bubbles: true,
            composed: true
        });
    this.dispatchEvent(showCustomLimit);
  }

  private _handleUnitChange(event: any) {
    const newUnit = event.target.id;
    if (this._unit === newUnit) return;
    this._unit = newUnit;

    if (newUnit === "kt") {
      this._convertToKt();
    } else if (newUnit === "m/s") {
      this._convertToMs();
    }

    this.requestUpdate();
  }

  private _shouldConvertUnit(elementName: string) {
    const convertibleElements = [
      "Wind10M",
      "Wind50M",
      "WindGusts10M",
      "WindGusts50M",
      "RiskWindSpeed"
    ];
    return convertibleElements.includes(elementName);
  }

  render() {
    const paths = [
      { name: 'Home page', action: this._onClose.bind(this) },
      { name: 'Heavy weather alarm limits', action: this._onBackFromHistory.bind(this) },
    ];

    if (this._viewingHistory) {
      paths.push({ name: 'Changes history', action: this._onViewHistory.bind(this) });
    }
    
    this._checkValidity();

    return html`
      <div class="container">
        <div class="header-container">
          <h1 class="header">
            ${
              this._viewingHistory
                ? "Heavy weather alarm limits: Changes history"
                : "Heavy weather alarm limits"
            }
          </h1>
          <div class="view-switch"> <mc-button  appearance="primary" variant="filled" fit="small" @click=${this._goToCustomView}>Switch to vessel custom limits</mc-button></div>
        </div>

          <div class="breadcrumb-container">

        <breadcrumb-navigation .paths=${paths}></breadcrumb-navigation>
          </div>
          ${
            this._viewingHistory
              ? html`
                  <voyage-view-history
                    .history="${this._history}"
                    @back="${this._onBackFromHistory}"
                  ></voyage-view-history>
                `
              : html` ${this._loading && this._heavyWeatherAlarmLimits !== null
                  ? html`<div class="spinner">
                      <mc-loading-indicator
                        fit="large"
                        label="Loading data..."
                      ></mc-loading-indicator>
                    </div>`
                  : html`
        <div class="radio-button-display">
            <div class="padding">Change wind unit:</div>
            <div class="padding">
                  <mc-radio
                    id="m/s"
                    name="windUnit"
                    label="m/s"
                    @change=${(evt:InputEvent) => this._handleUnitChange(evt)}
                    ?checked=${this._unit == "m/s"}
                  />
                </div>
                <div class="padding">
                <mc-radio
                    id="kt"
                    name="windUnit"
                    label="kt"
                    @change=${(evt:InputEvent) => this._handleUnitChange(evt)}
                    ?checked=${this._unit == "kt"}
                  />
                </div>
            <div>
            </div>
        </div>
        <div class="content">
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th rowspan="2">Parameter</th>
                            <th colspan="3">Maersk's policy</th>
                        </tr>
                        <tr>
                            <th class="header-right">TEU < 2500</th>
                            <th class="header-right">TEU [2500; 5000]</th>
                            <th class="header-right">TEU > 5000</th>
                        </tr>
                    </thead>
                    <tbody>
                    ${this._elements.map((element) => {
                    const unit = this._unit;
                    const shouldConvert = this._shouldConvertUnit(element.name);
                      return html`
                        <tr>
                          <td
                            class="cell ${element.connectedTo
                              ? "connected"
                              : "name"}"
                          >
                            ${element.text} ${shouldConvert ? `(${unit})` : `${element.unit}`}
                          </td>
                          <td class="inputContainer">
                            ${this._renderInputElement(
                              element,
                              "teuLessthan2500"
                            )}
                          </td>
                          <td class="inputContainer">
                            ${this._renderInputElement(
                              element,
                              "teuBetween2500to5000"
                            )}
                          </td>
                          <td class="inputContainer">
                            ${this._renderInputElement(
                              element,
                              "teuMorethan5000"
                            )}
                          </td>
                        </tr>
                      `;
                    })}
                    </tbody>
                </table>
              </div>
              ${this._isUpdated ? html`<div class="footer-text">Table data has been updated.</div>` : ''}
              ${this._isValid ? '' : html`<div class="footer-text-invalid">Please make sure there are no errors before clicking Apply.</div>`}
          </div>
      </div>
    <slot name="footer">
    <div id="footer" class="button-container">
    ${
      this._isEditing && this.hasWriteAccess
        ? html` <button
              class="footerButton cancel"
              @click=${this._onViewHistory}
            >
              View history
            </button>
            <button class="footerButton cancel" @click=${this._onCancel}>
              Cancel
            </button>
            <button class="footerButton accept ${!this._isValid ? "disabled" : ""}" ?disabled=${!this._isValid || !this.hasWriteAccess} @click=${this._onAccept}>
              Apply
            </button>`
        : html` <button
              class="footerButton cancel"
              @click=${this._onViewHistory}
            >
              View history
            </button>
            <button class="footerButton cancel ${!this.hasWriteAccess ? "disabled" : ""}" ?disabled=${!this.hasWriteAccess} @click=${this._onEditFields}>
              Edit fields
            </button>
            <button
              class="footerButton accept disabled"
              ?disabled=${this._isEditing}
            >
              Apply
            </button>`
    }
    </div>
    </slot>
</div>`}`
          }
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  private _heavyWeatherAlarmLimits: HeavyWeatherLimits;
  private _elements: HeavyWeatherLimitElement[];

  private _renderInputElement(element: HeavyWeatherLimitElement, key: string) {
    if (element.dropdown) {
      return this._renderDropdownInput(element, key);
    } else if (element.connectedTo) {
      return this._renderPeriodInput(element, key);
    } else {
      return this._renderNumberInput(element, key);
    }
  }

  @property({ type: Array }) _isValidInput: string[] = [];

  private _renderNumberInput(element: HeavyWeatherLimitElement, key: string) {
    const min = this._getMinValue(element, key);
    const prop = element.name;
    let max : number | undefined = element.max;
    if(this._unit === "kt" && (prop === "Wind10M" || prop === "Wind50M" || prop === "WindGusts10M" || prop === "WindGusts50M" || prop === "RiskWindSpeed")) {
      max = parseFloat((element.max! * 1.94384).toFixed(1));
    }
    
    const value = this._heavyWeatherAlarmLimits[prop][key as keyof TEU];

    const onValueInput = (evt: InputEvent) => {
      const target = <HTMLInputElement>evt.target;
      const inputValue = target.value === "" ? null : parseFloat(target.value);
      const newValue = parseFloat((evt.target as HTMLInputElement).value);
      if (isNaN(newValue)) {
        this._heavyWeatherAlarmLimits[element.name]![key as keyof TEU] = null;
      } else {
        this._heavyWeatherAlarmLimits[element.name]![key as keyof TEU] =
          newValue;
      }

      const propValue = this._heavyWeatherAlarmLimits[prop];

      if (propValue) {
        if (_isTEU(propValue)) {
          if (key in propValue) {
            propValue[key as keyof TEU] = newValue as number | null;
          }
        }
      }
      this._updateChangesData(element, key, inputValue);
      this._validateNumberInput(element, key, inputValue, min, max);
    };

    const minCondition = `${element.name}+${key}+min`;
    const maxCondition = `${element.name}+${key}+max`;

    return html`
      <div class="number-input">
        <input
          data-test=${element.name}
          class="cell numberInput"
          type="number"
          step="0.1"
          min=${min}
          max="${max ?? Infinity}"
          placeholder="Enter value"
          @input=${onValueInput}
          ?disabled=${!this._isEditing}
          .value="${value !== null && value !== undefined ? value : ""}"
        />
        ${this._isValidInput.includes(minCondition)
          ? html`
              <div class="number-validation-icon">
                <span class="questionCircle">${SvgIcons.customLightQuestionCircle}</span>
                <voyage-tooltip-proxy
                  text="Minimum value is ${min}"
                  html-content
                  .positionPriority=${["top"]}
                >
                </voyage-tooltip-proxy>
              </div>
            `
          : html`<voyage-tooltip
                  text="Minimum value is ${min}"
                  html-content
                  .positionPriority=${["top"]}
                >
                </voyage-tooltip>
          `}
        ${this._isValidInput.includes(maxCondition)
          ? html`
              <div class="number-validation-icon">
                <span class="questionCircle">${SvgIcons.customLightQuestionCircle}</span>
                <voyage-tooltip-proxy
                  text="Maximum value is ${max}"
                  html-content
                  .positionPriority=${["top"]}
                >
                </voyage-tooltip-proxy>
              </div>
            `
          : html``}
      </div>
    `;
  }

  private _renderPeriodInput(element: HeavyWeatherLimitElement, key: string) {
    const max = element.max;
    const prop = element.name;
    const onValueInput = (evt: InputEvent, type: "min" | "max") => {
      const target = evt.target as HTMLInputElement;
      const value = target.value !== "" ? Number(target.value) : null;
      (this._heavyWeatherAlarmLimits[prop] as any)[key][type] = value;

      this._updateChangesDataForPeriodInput(element, key, value, type);
      this._validatePeriodInput(value, element, key, type, max);

      this.requestUpdate();
    };

    return html`
      <div class="cell numberInput periodInput" connected>
        <div>from</div>
        <div class="number-input">
          <input
            data-test=${element.name + "-from"}
            class="inlineInput"
            type="number"
            step="1"
            min="0"
            max=${(this._heavyWeatherAlarmLimits[prop] as any)[key].max ?? ""}
            placeholder="Enter value"
            @input=${(e: InputEvent) => onValueInput(e, "min")}
            ?disabled=${!this._isEditing}
            value=${(this._heavyWeatherAlarmLimits[prop] as any)[key].min ?? ""}
          />
          ${this._isValidInput.includes(`${element.name}+${key}+min`)
            ? html`
                <div>
                  <span class="questionCircle">${SvgIcons.customLightQuestionCircle}</span>
                  <div>
                  <voyage-tooltip-proxy
                    text="'from' value is more than 'to' value"
                    html-content
                    .positionPriority=${["top"]}
                  >
                  </voyage-tooltip-proxy>
          </div>
                </div>
              `
            : html``}
        </div>

        <div>to</div>
        <div class="number-input">
          <input
            data-test=${element.name + "-to"}
            class="inlineInput size"
            type="number"
            step="1"
            min="0"
            max="${max ?? Infinity}"
            placeholder="Enter value"
            @input=${(e: InputEvent) => onValueInput(e, "max")}
            ?disabled=${!this._isEditing}
            value=${(this._heavyWeatherAlarmLimits[prop] as any)[key].max ?? ""}
          />
          ${this._isValidInput.includes(`${element.name}+${key}+max`)
            ? html`
                <div class="number-validation-icon">
                  <span class="questionCircle">${SvgIcons.customLightQuestionCircle}</span>
                  <voyage-tooltip-proxy
                    text="Maximum value is ${max}"
                    html-content
                    .positionPriority=${["top"]}
                  >
                  </voyage-tooltip-proxy>
                </div>
              `
            : html``}
        </div>
      </div>
    `;
  }

  private _renderDropdownInput(element: HeavyWeatherLimitElement, key: string) {
    const prop = element.name;
    const onValueInput = (evt: InputEvent) => {
      const target = <HTMLSelectElement>evt.target;
      const value = target.value === "" ? null : Number(target.value);
      const propValue = this._heavyWeatherAlarmLimits[prop];

      if (propValue) {
        if (_isTEU(propValue)) {
          if (key in propValue) {
            propValue[key as keyof TEU] = value as number | null;
          }
        }

        const selectedOption = element.dropdown!.find(option => option.value === value);
        const text = selectedOption ? selectedOption.text : null;
        this._updateChangesData(element, key, text);
      }
    };
    const selectedValue = (this._heavyWeatherAlarmLimits[prop] as any)[key];

    return html`
      <select
        data-test=${element.name + "-warn"}
        class="cell dropdown"
        @input=${(e: InputEvent) => onValueInput(e)}
        ?disabled=${!this._isEditing}
      >
        <option value="">Select</option>
        ${element.dropdown!.map(
        ({ value, text }) =>
          html`
            <option value=${value} ?selected=${value === selectedValue}>
              ${text}
            </option>
          `
      )}
      </select>
    `;
  }

  private _onClose() {
    this.dispatchEvent(new Event("cancel"));
    this._isEditing = false;
  }

  private _onCancel() {
    this._isEditing = false;
    this._isUpdated = false; 
  }

  private async _onAccept() {
    if (this._isValidInput.length > 0) return;
    let convertedValuesHeavyWeatherDataMs = {...this._heavyWeatherAlarmLimits};
    // convert to m/s
    if (this._unit === "kt") {
      const convertedHeavyWeatherLimitsData = Object.keys(
        this._heavyWeatherAlarmLimits
      ).reduce((acc, key) => {
        const value =
          this._heavyWeatherAlarmLimits[key as keyof HeavyWeatherLimits];

        if (typeof value === "object") {
          if (
            key === "Wind10M" ||
            key === "Wind50M" ||
            key === "WindGusts10M" ||
            key === "WindGusts50M" ||
            key === "RiskWindSpeed"
          ) {
            acc[key as keyof HeavyWeatherLimits] = Object.keys(value).reduce(
              (acc2, key2) => {
                const nestedValue = value[key2 as keyof typeof value];
                acc2[key2 as keyof typeof acc2] =
                  convertNestedValuesToMs(nestedValue);
                return acc2;
              },
              {} as any
            );
          }
        }
        return acc;
      }, {} as HeavyWeatherLimits);

       convertedValuesHeavyWeatherDataMs = {
        ...this._heavyWeatherAlarmLimits,
        ...convertedHeavyWeatherLimitsData,
      };
    } 

    let requestBody = {
      company_limits: convertedValuesHeavyWeatherDataMs,
      changes_data: this._changesData,
    };
    this._changesData = [];
    await VoyFleetAlarmModel!.setCompanyWeatherLimits(requestBody);
    this.dispatchEvent(new Event("accept"));
    this._isEditing = false;
    this._isUpdated = true;
  }

  private _convertToKt() {
    this._elements = this._elements.map((element) =>
      _convertElementToKt(element)
    );
    this._heavyWeatherAlarmLimits = this._convertHeavyWeatherLimitsData(
      convertNestedValuesToKt
    );
  }


  private _convertToMs() {
    this._elements = this._elements.map((element) =>
      _convertElementToMs(element)
    );
    this._heavyWeatherAlarmLimits = this._convertHeavyWeatherLimitsData(
      convertNestedValuesToMs
    );
  }

  private _convertHeavyWeatherLimitsData(
    conversionFunction: (
      value: number | { max: number | null; min: number | null } | null
    ) => any
  ): HeavyWeatherLimits {
    const convertedHeavyWeatherLimitsData: Partial<HeavyWeatherLimits> = {};

    keysToConvert.forEach((key) => {
      const value =
        this._heavyWeatherAlarmLimits[key as keyof HeavyWeatherLimits];
      if (value) {
        convertedHeavyWeatherLimitsData[key as keyof HeavyWeatherLimits] =
          Object.keys(value).reduce((acc, key2) => {
            const nestedValue = value[key2 as keyof typeof value];
            acc[key2 as keyof typeof acc] = conversionFunction(nestedValue);
            return acc;
          }, {} as any);
      }
    });

    return {
      ...this._heavyWeatherAlarmLimits,
      ...convertedHeavyWeatherLimitsData,
    };
  }

  private _updateChangesDataForPeriodInput(
    element: HeavyWeatherLimitElement,
    key: string,
    value: number | null,
    type: "min" | "max"
  ) {
    const prop = element.name;
    const displayKey = keyMapping[key] || key;
    const elementName = `${element.connectedToText} ${element.text} ${element.unit} / ${displayKey} ${type}`;
    const isElementExist = this._changesData.findIndex((item: any) => {
      return item.name === elementName;
    });
    const changeData = {
      to: value,
      from: (this._oldWeatherLimitsData[prop] as any)[key][type],
      name: elementName,
    };

    if (isElementExist !== -1) {
      this._changesData.splice(isElementExist, 1, changeData);
    } else {
      this._changesData = [...this._changesData, changeData];
    }
  }

  private _validatePeriodInput(
    value: number | null,
    element: HeavyWeatherLimitElement,
    key: string,
    type: "min" | "max",
    max: number | undefined
  ) {
    if (value !== null && (value < 0 || value > (max ?? Infinity))) {
      this._isValidInput.push(`${element.name}+${key}+${type}`);
    } else {
      const index = this._isValidInput.indexOf(
        `${element.name}+${key}+${type}`
      );
      if (index > -1) {
        this._isValidInput.splice(index, 1);
      }
    }
  }

  private _updateChangesData(
    element: HeavyWeatherLimitElement,
    key: string,
    value: number | string | null
  ) {
    const prop = element.name;
    const displayKey = keyMapping[key] || key;
    const unit = this._unit;
    const shouldConvert = this._shouldConvertUnit(element.name);
    const elementName = `${element.text} ${shouldConvert && (element.unit !== undefined)  ? `(${unit})` : `${element.unit}`} / ${displayKey}`;
    const isElementExist = this._changesData.findIndex(
      (item: any) => item.name === elementName
    );

    let changeData: { to: number | string | null; from: number |{ max: number | null; min: number | null; }| string | null; name: string };

    if(element.dropdown){
      const oldValue = (this._oldWeatherLimitsData[prop])[key as keyof TEU];
      const oldTextValue = element.dropdown!.find(option => option.value === oldValue)?.text || null;
      const newTextValue = typeof value === 'number'
    ? element.dropdown!.find(option => option.value === value)?.text || null
    : value;

      changeData = {
        to: newTextValue,
        from: oldTextValue,
        name: elementName,
      };
    }
    else {
      changeData = {
        to: value,
        from: (this._oldWeatherLimitsData[prop])[key as keyof TEU],
        name: elementName,
      };
    }
    

    if (isElementExist !== -1) {
      this._changesData.splice(isElementExist, 1, changeData);
    } else {
      this._changesData = [...this._changesData, changeData];
    }
  }

  private _validateNumberInput(
    element: HeavyWeatherLimitElement,
    key: string,
    value: number | null,
    min: number,
    max: number | undefined
  ) {
    const minCondition = `${element.name}+${key}+min`;
    const maxCondition = `${element.name}+${key}+max`;

    if (value !== null) {
      if (value < min) {
        if (!this._isValidInput.includes(minCondition)) {
          this._isValidInput = [...this._isValidInput, minCondition];
        }
      } else {
        this._isValidInput = this._isValidInput.filter(
          (item) => item !== minCondition
        );
      }

      if (value > (max ?? Infinity)) {
        if (!this._isValidInput.includes(maxCondition)) {
          this._isValidInput = [...this._isValidInput, maxCondition];
        }
      } else {
        this._isValidInput = this._isValidInput.filter(
          (item) => item !== maxCondition
        );
      }
    } else {
      this._isValidInput = this._isValidInput.filter(
        (item) => item !== minCondition && item !== maxCondition
      );
    }
  }

  private _onEditFields() {
    this._isEditing = !this._isEditing;
  }

  private _onViewHistory() {
    this._viewingHistory = true;
  }

  private _onBackFromHistory() {
    this._viewingHistory = false;
  }

  private async _checkValidity()
  {
        // Wait for render
        await this.updateComplete;

        // Check overall validity
        const inputs = <NodeListOf<HTMLInputElement>>this.shadowRoot!.querySelectorAll(`input[type="number"]`);
        let isValid = true;
        for (const input of inputs)
        {
            if (!input.validity.valid) {
                isValid = false;
                break;
            }
        }
        this._isValid = isValid;
  }

  private _getMinValue(element: HeavyWeatherLimitElement, key: string): number  {
    if (element.name === "Wind50M") {
      const value = wind50MMinValues[key as keyof TEU];
      if(this._unit === "m/s"){
      return typeof value === 'number' ? value : 0;
      } else if (this._unit === "kt"){
        return typeof value === 'number' ? parseFloat((value * 1.94384).toFixed(1)) : 0;
      }
    } else if (element.name === "Waves") {
      const value = wavesMinValues[key as keyof TEU];
      return typeof value === 'number' ? value : 0;
    }
    return element.min || 0;
  }
}
